import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewChildren,
    QueryList,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { TabPrimaryDirective } from '../../v2-layout/shared/directives/tab.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EMPTY, of, Subject } from 'rxjs';
import { catchError, debounceTime, switchMap, take } from 'rxjs/operators';
import moment from 'moment-timezone';

import { GableReloadRouteReuseStrategy } from '../../../services/gable/gable-reload-route-reuse.strategy';
import { MatDialogSizes } from '../../../enums/mat-dialog-sizes.enum';
import { JwtLegFiClaims } from '../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import {
    GableChatAttachment,
    GableChatMessage,
    GableDrawerService,
    GableMode,
} from '../../../services/gable/gable-drawer.service';
import { GableAttachmentUploaderService } from '../../../services/gable/gable-attachment-uploader.service';
import { GablePreference, GablePreferencesService } from '../../../services/gable/gable-preferences.service';
import {
    ActionPreviewCardPayload,
    ActionPreviewEdits,
    GableActivitySummaryItem,
    GableCitation,
    GableConversationSummary,
    GableFeedback,
    GableFeedbackChip,
    GablePendingProposal,
    GableQuestionAnswer,
    GableToolCard,
    GableService,
    BudgetRedirectCardPayload,
    DeliverableCardPayload,
    GenerateReportCardPayload,
    LedgerIntelligenceResult,
    isActionPreviewCardPayload,
    isBudgetRedirectCardPayload,
    isDeliverableCardPayload,
    isGenerateReportCardPayload,
    isLedgerIntelligenceCardPayload,
    gableCommitFailureMessage,
    isGableTerminalRefusal,
} from '../../../services/gable/gable.service';
import { PageContextService } from '../../../services/gable/page-context.service';
import { GableDrillDownOrg, GableScopeService } from '../../../services/gable/gable-scope.service';
import { PropertyManagementCompanyService } from '../../../services/property-management-company/property-management-company.service';
import { PmcOrganizationsService } from '../../../services/property-management-company/pmc-organizations.service';
import { Organization } from '../../../models/entities/organization';
import { LocalService } from '../../../services/storage/local.service';
import { GablePendingQuestions } from '../../../services/gable/gable-stream.service';
import { HighlightPipe } from '../../../pipes/highlight.pipe';
import { MarkdownPipe } from '../../../pipes/markdown.pipe';
import {
    ApprovalDialogComponent,
    ApprovalDialogData,
    ApprovalDialogResult,
} from '../../v2-layout/gable/approval-dialog/approval-dialog.component';
import {
    TeachGableDialogComponent,
    TeachGableDialogData,
    TeachGableDialogResult,
} from '../../v2-layout/gable/teach-gable-dialog/teach-gable-dialog.component';
import { GrowlerService } from '../../shared/growler/growler.service';
import { QuestionCardComponent } from '../../v2-layout/gable/question-card/question-card.component';
import { GableBudgetRedirectCardComponent } from '../../v2-layout/gable/budget-redirect-card/budget-redirect-card.component';
import { GableActionPreviewCardComponent } from '../../v2-layout/gable/action-preview/action-preview-card.component';
import { GableReportCardComponent } from '../../v2-layout/gable/report-card/report-card.component';
import { GableDeliverableCardComponent } from '../../v2-layout/gable/deliverable-card/deliverable-card.component';
import { GableLedgerIntelligenceCardComponent } from '../../v2-layout/gable/ledger-intelligence-card/ledger-intelligence-card.component';
import { AiGableMascotComponent } from '../../v2-layout/shared/ai-gable-mascot/ai-gable-mascot.component';
// AiGableMascotComponent typed as a ViewChild ref; keep this import
// even though it looks duplicate — Angular needs both the runtime
// import (for the standalone component slot) and the type for the
// ViewChild generic below.
import { AiThoughtTrailComponent } from '../../v2-layout/shared/ai-thought-trail/ai-thought-trail.component';
import { AlertBlockComponent } from '../../v2-layout/shared/alert-block/alert-block.component';
import { FlexSpacerDirective } from '../../v2-layout/shared/directives/flex-spacer.directive';
import { GableDrawerResizeDirective } from '../directives/gable-drawer-resize.directive';
import { GableMessageHighlightDirective } from './gable-message-highlight.directive';
import { GableDownloadLinkDirective } from './gable-download-link.directive';
import { GableFeedbackPanelComponent } from '../gable-feedback-panel/gable-feedback-panel.component';
import { GableActivityPanelComponent } from '../gable-activity-panel/gable-activity-panel.component';
import { GABLE_HERO } from '../gable-autopilot-mock';
import {
    GableActivityFilter,
    MODULE_LABELS,
    moduleForActionType,
} from '../gable-activity-panel/gable-activity-panel.component';
import {
    GableSuggestionCardComponent,
    GableSuggestionCardPayload,
} from '../gable-suggestion-card/gable-suggestion-card.component';
import { GableSuggestionsPipe } from '../../../pipes/gable-suggestions.pipe';
import { GableSkillsPanelComponent } from '../gable-skills-panel/gable-skills-panel.component';
import { SimpleBadgeComponent } from '../../v2-layout/shared/simple-badge/simple-badge.component';
import {
    ConfirmActionV2DialogComponent,
    ConfirmActionV2DialogInput,
} from '../../v2-layout/shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';

interface GableQuickAction {
    // 'page' is dynamic — only present when the server returns page-aware
    // starter suggestions for the current route (see loadWelcomeSuggestions).
    id: 'page' | 'popular' | 'finance' | 'reports' | 'units' | 'violations' | 'requests' | 'comms';
    label: string;
    icon: string | null;
}

interface GableSuggestion {
    label: string;
    prompt: string;
}

/**
 * Per-module card presentation for the empty-state grid. Add a module here and it
 * renders automatically once it has pending items — nothing else to touch.
 *
 * `tone` keys the icon-tile tint in SCSS; the palette is the Autopilot mock's.
 */
export const GABLE_MODULE_CARDS: Record<
    Exclude<GableActivityFilter, 'all'>,
    { icon: string; tone: string; tagline: string }
> = {
    requests: {
        icon: 'ph-wrench',
        tone: 'cyan',
        tagline: 'Triage and reply with suggested decisions',
    },
    violations: {
        icon: 'ph-scales',
        tone: 'orange',
        tagline: 'Reminders, escalations and fines to approve',
    },
    payments: {
        icon: 'ph-bank',
        tone: 'green',
        tagline: 'Match deposits, categorize, flag anything odd',
    },
    communications: {
        icon: 'ph-chat-circle-text',
        tone: 'indigo',
        tagline: 'Drafted messages waiting to send',
    },
    amenities: {
        icon: 'ph-swimming-pool',
        tone: 'cyan',
        tagline: 'Booking approvals and cancellations',
    },
    calendar: {
        icon: 'ph-calendar-check',
        tone: 'indigo',
        tagline: 'Events ready to add to the calendar',
    },
    surveys: {
        icon: 'ph-clipboard-text',
        tone: 'green',
        tagline: 'Sends and closes waiting on you',
    },
    voting: {
        icon: 'ph-checks',
        tone: 'orange',
        tagline: 'Ballot reminders and results to share',
    },
};

/**
 * Omnipresent Gable AI drawer — slides in from the right edge over the
 * router-outlet on every authenticated route. Thin view over
 * `GableDrawerService` (which owns conversation, streaming, width, etc.).
 *
 * Layout:
 *   [ header — title, dock-toggle, close ]
 *   [ scrollable body — empty-state OR conversation OR pending approvals ]
 *   [ composer — textarea + send button ]
 */
@UntilDestroy()
@Component({
    selector: 'app-gable-drawer-host',
    templateUrl: './gable-drawer-host.component.html',
    styleUrls: ['./gable-drawer-host.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        ScrollingModule,
        MatButtonModule,
        MatDialogModule,
        MatDividerModule,
        MatMenuModule,
        MatTooltipModule,
        MatTabsModule,
        TabPrimaryDirective,
        MatFormFieldModule,
        MatInputModule,
        AiGableMascotComponent,
        AiThoughtTrailComponent,
        AlertBlockComponent,
        FlexSpacerDirective,
        GableActionPreviewCardComponent,
        GableBudgetRedirectCardComponent,
        GableDrawerResizeDirective,
        GableMessageHighlightDirective,
        GableDownloadLinkDirective,
        GableFeedbackPanelComponent,
        GableReportCardComponent,
        GableDeliverableCardComponent,
        GableLedgerIntelligenceCardComponent,
        GableActivityPanelComponent,
        GableSkillsPanelComponent,
        GableSuggestionCardComponent,
        GableSuggestionsPipe,
        HighlightPipe,
        MarkdownPipe,
        QuestionCardComponent,
        TeachGableDialogComponent,
        SimpleBadgeComponent,
    ],
})
export class GableDrawerHostComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChild('messageInput') messageInputRef?: ElementRef<HTMLTextAreaElement>;
    @ViewChild('attachmentFileInput') attachmentFileInputRef?: ElementRef<HTMLInputElement>;
    @ViewChild('communityResultsList') communityResultsRef?: ElementRef<HTMLElement>;
    @ViewChild('conversationScroller') conversationScrollerRef?: ElementRef<HTMLElement>;
    @ViewChild('emptyMascot') emptyMascotRef?: AiGableMascotComponent;
    @ViewChildren(GableActionPreviewCardComponent)
    actionPreviewCardsRef?: QueryList<GableActionPreviewCardComponent>;

    /** Quick-action chip strip — drives the suggestion list rendered below. */
    readonly quickActions: ReadonlyArray<GableQuickAction> = [
        { id: 'popular', label: 'Popular', icon: 'ph-trend-up' },
        { id: 'finance', label: 'Finance', icon: 'ph-currency-circle-dollar' },
        { id: 'reports', label: 'Reports', icon: 'ph-file-text' },
        { id: 'units', label: 'Units', icon: 'ph-house-line' },
        { id: 'violations', label: 'Violations', icon: 'ph-seal-warning' },
        { id: 'requests', label: 'Requests', icon: 'ph-seal-question' },
        { id: 'comms', label: 'Comms', icon: 'ph-megaphone' },
    ];

    /** Suggestion sets per quick-action chip. */
    private readonly _suggestionsByAction: Record<Exclude<GableQuickAction['id'], 'page'>, GableSuggestion[]> = {
        popular: [
            { label: 'Show me all open violations', prompt: 'Show me all open violations.' },
            { label: 'Which units have the most violations?', prompt: 'Which units have the most violations?' },
            { label: 'What can you tell me about my organization\'s data?', prompt: 'What can you tell me about my organization\'s data?' },
        ],
        finance: [
            { label: 'Show me this month\'s deposits', prompt: 'Show me this month\'s deposits.' },
            { label: 'Which owners are most past due?', prompt: 'Which owners are most past due?' },
        ],
        reports: [
            { label: 'Generate a delinquency report', prompt: 'Generate a delinquency report for the current period.' },
            { label: 'Generate a violation summary report', prompt: 'Generate a violation summary report.' },
        ],
        units: [
            { label: 'List all units missing an owner', prompt: 'List all units that don\'t currently have an owner assigned.' },
            { label: 'Which units have the most violations?', prompt: 'Which units have the most violations?' },
            { label: 'Show me units with overdue balances', prompt: 'Show me units with overdue balances.' },
        ],
        violations: [
            { label: 'Show me all open violations', prompt: 'Show me all open violations.' },
            { label: 'Which violation categories are most common?', prompt: 'Which violation categories are most common right now?' },
            { label: 'List violations created in the last 30 days', prompt: 'List violations created in the last 30 days.' },
        ],
        requests: [
            { label: 'Show me all open requests', prompt: 'Show me all open architectural and maintenance requests.' },
            { label: 'How many requests have been resolved this month?', prompt: 'How many requests have been resolved this month?' },
        ],
        comms: [
            { label: 'Draft a community-wide reminder about pool hours', prompt: 'Draft a community-wide reminder about pool hours.' },
            { label: 'Send a courtesy warning to specific units', prompt: 'Help me send a courtesy warning to specific units.' },
        ],
    };

    /** Page-aware starter suggestions (Haiku, keyed to the current route).
     *  Empty until loadWelcomeSuggestions() resolves; drives the dynamic
     *  "For this page" chip that fronts the quick-action strip when present. */
    pageSuggestions: GableSuggestion[] = [];
    private _welcomeLoadedForUrl: string | null = null;

    /** PMC name for the portfolio scope banner (null until propertyManagementCompany$ resolves). */
    pmcName: string | null = null;

    // ----- Community picker / drill-down (SD-9209) -----
    communitySearch = '';
    communityResults: Organization[] = [];
    communityLoading = false;
    /** True once a picker search has resolved — gates the "no matches" copy. */
    communitySearched = false;
    private readonly _communitySearch$ = new Subject<string>();

    isOpen = false;
    widthPx = 400;
    mode: GableMode = 'drawer';
    messages: GableChatMessage[] = [];

    /** True once the in-flight turn has told the user it moved to the background — chat is handed back. */
    get backgroundMode(): boolean {
        return this.messages.some((m) => m.streamState === 'streaming' && !!m.longRunNotice);
    }

    private _wasBackgroundMode = false;

    /**
     * Index of the FIRST assistant message — the only one that shows the
     * Gable mascot. Repeating it on every reply read as a new "Gable is
     * here" each turn; the mascot is an introduction, shown once.
     */
    get firstAssistantIndex(): number {
        return this.messages.findIndex((m) => m.role === 'assistant');
    }
    pendingProposals: GablePendingProposal[] = [];
    apiSuggestions: string[] = [];
    pendingQuestions: GablePendingQuestions | null = null;
    isSending = false;
    turnInFlightNotice = false;
    draftMessage = '';

    // ----- PDF attachment (SD-9309) -----
    pendingAttachment: GableChatAttachment | null = null;
    attachmentUploading = false;

    // ----- Preferences tab (SD-9302) -----
    preferences: GablePreference[] = [];
    preferencesLoading = false;

    preferencesLoadFailed = false;
    newPreferenceText = '';
    savingPreference = false;

    // ----- Chat history (SD-6742) -----
    activeConversations: GableConversationSummary[] = [];
    archivedConversations: GableConversationSummary[] = [];
    activeTitle: string | null = null;
    historyLoading = false;
    /** True while a paged active-list fetch (search reset / load-more) runs. */
    conversationsLoading = false;
    /** Server reports another page of recent chats is available. */
    hasMoreConversations = false;
    /** Whether the expanded-mode sidebar is showing archived vs recent chats. */
    showArchived = false;
    /** Conversation whose title is being edited inline in the sidebar, if any. */
    editingConversationId: number | null = null;
    /** Working value for the inline rename input. */
    editingTitle = '';
    /** Cap the drawer-mode history menu to the N most-recent chats. */
    private static readonly DRAWER_MENU_RECENT_LIMIT = 10;
    /**
     * Search term for the expanded sidebar recent list. Server-side now
     * (SD-6742) — the bound value is debounced into a server call rather
     * than filtering the in-memory list. Kept as a field so the input stays
     * controlled and the empty-state copy can branch on "is a search active".
     */
    historySearch = '';
    /** px-from-bottom threshold that triggers the next-page fetch. */
    private static readonly INFINITE_SCROLL_THRESHOLD_PX = 80;

    /**
     * Auto-scroll bookkeeping.
     *
     * - When a new user message appears, scroll it to the TOP of the visible
     *   drawer so the user sees "the last thing I asked about" and watches
     *   the response stream in below it.
     * - While an assistant bubble is streaming AND the user hasn't scrolled
     *   up to read history, keep the bottom glued so new tokens stay visible.
     * - If the user scrolls up, stop force-sticking — they're reading. As
     *   soon as they scroll back near the bottom, re-stick.
     */
    private _lastUserMessageIndex = -1;
    private _isPinnedToBottom = true;
    private _lastScrollHeight = 0;
    /** Px-from-bottom under which "pinned" reactivates after the user
     *  scrolls back near the tail. Kept tight so we don't yank from
     *  partway up the history. */
    private static readonly STICK_THRESHOLD_PX = 40;

    /** Tracks which message currently has its inline feedback panel open. */
    private _feedbackPanelOpenFor: number | null = null;

    /**
     * When the user clicks Teach Gable from a thumbs-down panel, we stash
     * the source message here so we can resolve the close result back
     * onto the right interactionId (drawer mode swaps inline, so the
     * MatDialog `afterClosed` chain isn't carrying the msg reference).
     */
    private _pendingTeachMsg: GableChatMessage | null = null;

    /** Whether the inline Teach Gable view is showing in the drawer body. */
    drawerView: 'chat' | 'teach' = 'chat';

    // -------------------------------------------------------------------------
    // Autopilot preview UI (SD-7384 north star — demo surface)
    // -------------------------------------------------------------------------

    /**
     * Kill switch for the Autopilot preview chrome (tabs, hero, status line).
     * Default ON; disable instantly from the console by storing the key
     * 'gable-autopilot-ui' = 'off' + reload if the new chrome misbehaves
     * mid-demo. Chat itself is untouched either way.
     */
    readonly autopilotUi: boolean = LocalService.get('gable-autopilot-ui') !== 'off';

    /** Which drawer tab is showing. Chat is live; Activity renders the
     *  review-queue/feed preview (SD-7969 wires it to real data). No
     *  Autopilot settings tab — suggestions-only per the 2026-07-11
     *  product decision. */
    activeTab: 'chat' | 'activity' | 'skills' | 'prefs' = 'chat';

    /** Hero empty-state copy (tagline + status strip). The header status
     *  line, tab badge, and module quick links are all real counts now. */
    readonly apHero = GABLE_HERO;

    /** Module filter handed to the Activity panel (set by module quick links). */
    activityFilter: GableActivityFilter = 'all';

    /** Per-action-type counts behind the hero module quick links. */
    activitySummary: GableActivitySummaryItem[] = [];

    /** Template alias so the HTML can read teachData without leading underscore. */
    get teachData(): TeachGableDialogData | null {
        return this._drawer.teachData;
    }

    constructor(
        private _drawer: GableDrawerService,
        private _dialog: MatDialog,
        private _cdr: ChangeDetectorRef,
        private _aiPoc: GableService,
        private _growler: GrowlerService,
        private _pageContext: PageContextService,
        private _router: Router,
        private _reuseStrategy: GableReloadRouteReuseStrategy,
        private _scope: GableScopeService,
        private _pmcService: PropertyManagementCompanyService,
        private _pmcOrgs: PmcOrganizationsService,
        private _attachmentUploader: GableAttachmentUploaderService,
        private _preferencesService: GablePreferencesService,
    ) {}

    ngOnInit(): void {
        // A Gable write committed (from the chat card or the Activity panel) →
        // soft-reload the page behind the drawer so it reflects the change,
        // without a hard reload (keeps the shell + drawer + chat mounted).
        // The one-shot reuse-strategy flag forces the routed page to re-init
        // and re-run its own ngOnInit fetch; no per-page wiring. SD-8019.
        this._drawer.actionCommitted$.pipe(untilDestroyed(this)).subscribe(() => {
            const url = this._router.url;
            if (!url || url.startsWith('/auth')) {
                return;
            }
            this._reuseStrategy.setForceReload(true);
            void this._router
                .navigateByUrl(url)
                .finally(() => this._reuseStrategy.setForceReload(false));
        });
        this._drawer.isOpen$.pipe(untilDestroyed(this)).subscribe((open) => {
            this.isOpen = open;
            // Opening in the empty state → fetch page-aware starter chips for
            // the current route (best-effort, de-duped per URL).
            if (open && this.isEmptyState) {
                this.loadWelcomeSuggestions();
            }
            this._cdr.markForCheck();
        });
        // Navigating with the drawer open + empty → refresh the "For this
        // page" chips for the new route (de-duped per URL inside).
        this._pageContext.context$.pipe(untilDestroyed(this)).subscribe(() => {
            if (this.isOpen && this.isEmptyState) {
                this.loadWelcomeSuggestions();
            }
        });
        // Scope boundary crossing (SD-9208): drop stale chips + refetch under the new lane.
        this._scope.scopeKey$.pipe(untilDestroyed(this)).subscribe(() => {
            this._welcomeLoadedForUrl = null;
            this.pageSuggestions = [];
            this._resetCommunityPicker();
            // Hero counts/tiles follow the scope: refetch the summary for the new lane.
            this.activitySummary = [];
            this.loadActivitySummary();
            if (this.isOpen && this.isEmptyState) {
                this.loadWelcomeSuggestions();
            }
            this._cdr.markForCheck();
        });
        // Community picker typeahead (SD-9209) — server search, 25 cap.
        this._communitySearch$
            .pipe(
                debounceTime(250),
                switchMap((term) => {
                    const pmcId = this._scope.pmcId;
                    if (pmcId === null) {
                        return of(null);
                    }
                    this.communityLoading = true;
                    this._cdr.markForCheck();
                    return this._pmcOrgs
                        .getPropertyManagementOrganizations(pmcId, term, 'name', 'asc', 1, 25)
                        .pipe(catchError(() => of(null)));
                }),
                untilDestroyed(this),
            )
            .subscribe((res) => {
                this.communityLoading = false;
                this.communitySearched = res !== null;
                this.communityResults = res?.data ?? [];
                this._cdr.markForCheck();
                if (this.communityResults.length > 0) {
                    this._scrollCommunityResultsIntoView();
                }
            });
        this._pmcService.propertyManagementCompany$.pipe(untilDestroyed(this)).subscribe((pmc) => {
            this.pmcName = pmc?.name ?? null;
            this._cdr.markForCheck();
        });
        this._drawer.widthPx$.pipe(untilDestroyed(this)).subscribe((px) => {
            this.widthPx = px;
            this._cdr.markForCheck();
        });
        this._drawer.mode$.pipe(untilDestroyed(this)).subscribe((m) => {
            this.mode = m;
            this._cdr.markForCheck();
        });
        this._drawer.drawerView$.pipe(untilDestroyed(this)).subscribe((v) => {
            this.drawerView = v;
            this._cdr.markForCheck();
        });
        // Programmatic seeds (SD-9233) must land on the chat tab, not a lingering Activity/Skills panel.
        this._drawer.chatViewRequested$.pipe(untilDestroyed(this)).subscribe(() => {
            this.activeTab = 'chat';
            this._cdr.markForCheck();
        });
        this._drawer.activityViewRequested$.pipe(untilDestroyed(this)).subscribe((filter) => {
            this.onSelectTab('activity', filter);
        });
        this._drawer.messages$.pipe(untilDestroyed(this)).subscribe((m) => {
            this.messages = m;
            const bg = this.backgroundMode;
            if (bg && !this._wasBackgroundMode) {
                setTimeout(() => this.messageInputRef?.nativeElement.focus(), 60);
            }
            this._wasBackgroundMode = bg;
            this._cdr.markForCheck();
        });
        this._drawer.pendingProposals$.pipe(untilDestroyed(this)).subscribe((p) => {
            this.pendingProposals = p;
            this._cdr.markForCheck();
        });
        this._drawer.pendingQuestions$.pipe(untilDestroyed(this)).subscribe((q) => {
            this.pendingQuestions = q;
            this._cdr.markForCheck();
        });
        this._drawer.isSending$.pipe(untilDestroyed(this)).subscribe((s) => {
            this.isSending = s;
            this._cdr.markForCheck();
        });
        this._drawer.turnInFlightNotice$.pipe(untilDestroyed(this)).subscribe((n) => {
            this.turnInFlightNotice = n;
            this._cdr.markForCheck();
        });
        this._drawer.draftMessage$.pipe(untilDestroyed(this)).subscribe((d) => {
            this.draftMessage = d;
            this._cdr.markForCheck();
        });
        this._drawer.pendingAttachment$.pipe(untilDestroyed(this)).subscribe((a) => {
            this.pendingAttachment = a;
            this._cdr.markForCheck();
        });
        this._drawer.feedback$.pipe(untilDestroyed(this)).subscribe(() => {
            this._cdr.markForCheck();
        });
        this._drawer.apiSuggestions$.pipe(untilDestroyed(this)).subscribe((s) => {
            this.apiSuggestions = s;
            this._cdr.markForCheck();
        });
        this._drawer.activeConversations$.pipe(untilDestroyed(this)).subscribe((c) => {
            this.activeConversations = c;
            this._cdr.markForCheck();
        });
        this._drawer.archivedConversations$.pipe(untilDestroyed(this)).subscribe((c) => {
            this.archivedConversations = c;
            this._cdr.markForCheck();
        });
        this._drawer.activeTitle$.pipe(untilDestroyed(this)).subscribe((t) => {
            this.activeTitle = t;
            this._cdr.markForCheck();
        });
        this._drawer.historyLoading$.pipe(untilDestroyed(this)).subscribe((l) => {
            this.historyLoading = l;
            this._cdr.markForCheck();
        });
        this._drawer.conversationsLoading$.pipe(untilDestroyed(this)).subscribe((l) => {
            this.conversationsLoading = l;
            this._cdr.markForCheck();
        });
        this._drawer.hasMoreConversations$.pipe(untilDestroyed(this)).subscribe((h) => {
            this.hasMoreConversations = h;
            this._cdr.markForCheck();
        });

        this._drawer.loadPendingProposals();
        this.loadActivitySummary();
        this._drawer.refreshConversations();
    }

    ngOnDestroy(): void {
        // Service owns stream cleanup — host doesn't tear down chat state on
        // navigation. (Drawer host is mounted at app root so this only fires
        // on full logout / app teardown.)
    }

    ngAfterViewChecked(): void {
        const scroller = this.conversationScrollerRef?.nativeElement;
        if (!scroller) {
            return;
        }

        // Empty state (hero) — never auto-pin to the bottom; the greeting
        // and status strip live at the top and must be what the user sees
        // when the drawer opens.
        if (this.isEmptyState) {
            this._lastScrollHeight = scroller.scrollHeight;
            return;
        }

        // 1. If the user just submitted a new question, pin their bubble to
        //    the top of the visible drawer so the streaming reply unfolds
        //    underneath. This wins over "stick to bottom" — the user wants
        //    to see what they asked.
        const lastUserIndex = this._findLastUserMessageIndex();
        if (lastUserIndex !== -1 && lastUserIndex !== this._lastUserMessageIndex) {
            this._lastUserMessageIndex = lastUserIndex;
            const bubble = scroller.querySelector<HTMLElement>(
                `[data-user-msg-index="${lastUserIndex}"]`,
            );
            if (bubble) {
                // Top-align with a small gap from the body's top padding.
                scroller.scrollTop = bubble.offsetTop - scroller.offsetTop - 12;
                this._isPinnedToBottom = false;
                this._lastScrollHeight = scroller.scrollHeight;
                return;
            }
        }

        // 2. Only re-pin to the bottom when CONTENT GREW. CD fires for many
        //    reasons (mouse moves, scroll events, sibling state changes) —
        //    blindly resetting scrollTop on every tick fights the user when
        //    they scroll up after streaming ends. Comparing scrollHeight
        //    tells us "is there new content the user might want to see?".
        const grew = scroller.scrollHeight > this._lastScrollHeight;
        if (grew && this._isPinnedToBottom) {
            scroller.scrollTop = scroller.scrollHeight;
        }
        this._lastScrollHeight = scroller.scrollHeight;
    }

    /**
     * Bound from the template. Tracks whether the user has scrolled away
     * from the bottom so the streaming auto-scroll only kicks in when
     * they're actually watching the tail of the conversation.
     */
    onScroll(): void {
        const scroller = this.conversationScrollerRef?.nativeElement;
        if (!scroller) {
            return;
        }
        const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
        this._isPinnedToBottom = distanceFromBottom <= GableDrawerHostComponent.STICK_THRESHOLD_PX;
    }

    private _findLastUserMessageIndex(): number {
        for (let i = this.messages.length - 1; i >= 0; i--) {
            if (this.messages[i].role === 'user') {
                return i;
            }
        }
        return -1;
    }

    // -------------------------------------------------------------------------
    // Header chrome
    // -------------------------------------------------------------------------

    get firstName(): string {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (!jwt?.givenNames) {
            return 'there';
        }
        return jwt.givenNames.trim().split(/\s+/)[0] ?? 'there';
    }

    get orgName(): string {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        return jwt?.orgName ?? 'your organization';
    }

    /** Portfolio mode (SD-9208) — the drawer talks to the PMC lane and shows the scope banner. */
    get isPmcScoped(): boolean {
        return this._scope.isPmcScoped;
    }

    /** Drill-down community while PMC-scoped (SD-9209), else null. */
    get drillDownOrg(): GableDrillDownOrg | null {
        return this._scope.drillDownOrg;
    }

    get composerPlaceholder(): string {
        const drill = this.drillDownOrg;
        if (drill) {
            return `Ask about ${drill.name}…`;
        }
        return this.isPmcScoped ? 'Ask about your portfolio…' : `Ask about ${this.orgName}…`;
    }

    onCommunitySearchChange(term: string): void {
        this.communitySearch = term ?? '';
        this._communitySearch$.next(this.communitySearch);
    }

    /** First focus fetches the initial 25 communities without requiring a keystroke. */
    onCommunitySearchFocus(): void {
        if (!this.communitySearched && !this.communityLoading) {
            this._communitySearch$.next(this.communitySearch);
            return;
        }
        if (this.communityResults.length > 0) {
            this._scrollCommunityResultsIntoView();
        }
    }

    /** The result list renders below the fold (composer hides it) — reveal it once it exists. */
    private _scrollCommunityResultsIntoView(): void {
        setTimeout(() => {
            this.communityResultsRef?.nativeElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
    }

    onPickCommunity(org: Organization): void {
        this._scope.enterDrillDown({ id: org.id, name: org.name });
    }

    onExitDrillDown(): void {
        this._scope.clearDrillDown();
    }

    trackByOrgId(_: number, org: Organization): number {
        return org.id;
    }

    private _resetCommunityPicker(): void {
        this.communitySearch = '';
        this.communityResults = [];
        this.communityLoading = false;
        this.communitySearched = false;
    }

    get isEmptyState(): boolean {
        return this.messages.length === 0;
    }

    get hasPendingProposals(): boolean {
        return this.pendingProposals.length > 0;
    }

    get hasStreamingAssistant(): boolean {
        return this.messages.some((m) => m.role === 'assistant' && m.streamState === 'streaming');
    }

    /**
     * True while any sandbox deliverable in the conversation is still
     * generating (SD-7240). Used to hold back the thumbs/Teach footer +
     * feedback panel until the document card resolves — telemetry on a
     * turn that's still visibly working looks broken. The messages array
     * reference changes when the sandbox resolves (messages$ emission),
     * so OnPush re-evaluates this without manual nudging.
     */
    get hasPendingSandboxCard(): boolean {
        return this.messages.some((m) => m.sandboxState === 'generating');
    }

    /**
     * Empty-state suggestion list: page-aware starters only (per Catherine
     * 2026-07-11 — no topic chips, no section header). Popular is the quiet
     * fallback so routes without starters aren't blank.
     */
    get currentSuggestions(): GableSuggestion[] {
        if (this.pageSuggestions.length > 0) {
            return this.pageSuggestions;
        }
        // Portfolio scope renders server chips only — the static sets are single-org prompts (SD-9208).
        // Drill-down targets one community, so the static fallback is valid there (SD-9209).
        return this.isPmcScoped && !this.drillDownOrg ? [] : this._suggestionsByAction['popular'] ?? [];
    }

    /**
     * Fetch page-aware starter chips for the current route (best-effort;
     * silent fallback to the static "Popular" set). Called when the drawer
     * opens in its empty state; de-duped per URL so reopening is free.
     */
    private loadWelcomeSuggestions(): void {
        const url = this._pageContext.snapshot()?.url ?? null;
        if (!url || url === this._welcomeLoadedForUrl) {
            return;
        }
        this._welcomeLoadedForUrl = url;
        this._aiPoc
            .welcomeSuggestions()
            .pipe(untilDestroyed(this))
            .subscribe((res) => {
                // Ignore a stale response: if the route changed (or a newer
                // fetch started) while this one was in flight, drop it — else
                // a slow dashboard reply could overwrite the fast violations
                // one after navigation.
                if (this._welcomeLoadedForUrl !== url) {
                    return;
                }
                const starters = (res.groups ?? []).find((g) => g.kind === 'page_starter');
                this.pageSuggestions = (starters?.suggestions ?? []).map((s) => ({
                    label: s.label,
                    prompt: s.prompt,
                }));
                this._cdr.markForCheck();
            });
    }

    get drawerStyle(): { [k: string]: string } {
        // `--gable-width` drives drawer mode (resizable). `--gable-expanded-width`
        // drives expanded mode — fixed to the Figma spec (1140px = 248 sidebar +
        // 892 chat) via the shared MatDialogSizes enum, capped to viewport in SCSS.
        return {
            '--gable-width': `${this.widthPx}px`,
            '--gable-expanded-width': MatDialogSizes.GABLE_EXPANDED,
        };
    }

    onClose(): void {
        this._drawer.close();
    }

    onNewChat(): void {
        this.activeTab = 'chat';
        this._drawer.startNewConversation();
    }

    // -------------------------------------------------------------------------
    // Autopilot preview UI — tabs + hero
    // -------------------------------------------------------------------------

    onSelectTab(tab: 'chat' | 'activity' | 'skills' | 'prefs', filter: GableActivityFilter = 'all'): void {
        this.activeTab = tab;
        this.activityFilter = filter;
        if (tab === 'prefs') {
            this.loadPreferences();
        }
        this._cdr.markForCheck();
    }

    /** Compact-mode tab strip index ↔ tab name (Skills only occupies index 3, super-only). */
    get compactTabIndex(): number {
        if (this.activeTab === 'activity') { return 1; }
        if (this.activeTab === 'prefs') { return 2; }
        if (this.activeTab === 'skills' && this.isSuperUser) { return 3; }
        return 0;
    }

    onCompactTabIndexChange(index: number): void {
        if (index === 1) { this.onSelectTab('activity'); return; }
        if (index === 2) { this.onSelectTab('prefs'); return; }
        if (index === 3 && this.isSuperUser) { this.onSelectTab('skills'); return; }
        this.onSelectTab('chat');
    }

    // -------------------------------------------------------------------------
    // Preferences (SD-9302)
    // -------------------------------------------------------------------------

    /** Refetch on every tab activation (SD-9302) — chat's save_gable_preference tool can add rows mid-session. */
    loadPreferences(): void {
        if (this.preferencesLoading) {
            return;
        }
        this._fetchPreferences();
    }

    retryLoadPreferences(): void {
        this._fetchPreferences();
    }

    private _fetchPreferences(): void {
        this.preferencesLoadFailed = false;
        this.preferencesLoading = true;
        this._cdr.markForCheck();
        this._preferencesService.list().pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.preferences = res.preferences ?? [];
                this.preferencesLoading = false;
                this._cdr.markForCheck();
            },
            error: () => {
                this.preferencesLoadFailed = true;
                this.preferencesLoading = false;
                this._cdr.markForCheck();
            },
        });
    }

    onAddPreference(): void {
        const text = this.newPreferenceText.trim();
        if (!text || this.savingPreference) {
            return;
        }
        this.savingPreference = true;
        this._preferencesService.create(text).pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.savingPreference = false;
                this.preferences = res.preferences ?? [];
                this.newPreferenceText = '';
                this._cdr.markForCheck();
            },
            error: (err) => {
                this.savingPreference = false;
                this._growler.fromErrorResponse(err);
                this._cdr.markForCheck();
            },
        });
    }

    onDeletePreference(pref: GablePreference): void {
        const input: ConfirmActionV2DialogInput = {
            title: 'Delete this preference?',
            content: 'Gable will stop applying this instruction in future chats.',
            confirmButton: 'Delete',
        };
        this._dialog
            .open(ConfirmActionV2DialogComponent, {
                panelClass: ['v2-dialog'],
                width: ConfirmActionV2DialogComponent.width,
                data: input,
            })
            .afterClosed()
            .pipe(
                take(1),
                switchMap((result) => (result?.success ? this._preferencesService.remove(pref.id) : EMPTY)),
                untilDestroyed(this),
            )
            .subscribe({
                next: (res) => {
                    this.preferences = res.preferences ?? this.preferences.filter((p) => p.id !== pref.id);
                    this._cdr.markForCheck();
                },
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    trackByAttachmentFileId(_: number, att: { uploadedFileId: number }): number {
        return att.uploadedFileId;
    }

    trackByPreferenceId(_: number, p: GablePreference): number {
        return p.id;
    }

    /**
     * Hero module quick links — one per module that actually has activity
     * (pending or decided), built from the activity-summary counts. Click
     * opens the Activity tab pre-filtered to that module.
     */
    get moduleLinks(): {
        module: GableActivityFilter;
        label: string;
        description: string;
        tagline: string;
        icon: string;
        tone: string;
    }[] {
        const counts = new Map<string, { pending: number; decided: number }>();
        // Decided counts come from the (cached) activity summary…
        for (const item of this.activitySummary) {
            const module = moduleForActionType(item.actionType);
            if (module === 'other') {
                continue;
            }
            const prior = counts.get(module) ?? { pending: 0, decided: 0 };
            counts.set(module, { pending: prior.pending, decided: prior.decided + item.decided });
        }
        // …but the PENDING count is derived live from pendingProposals$ (grouped
        // via moduleForActionType) so a module card's "N items to review" ticks
        // down the moment an item is approved/dismissed, without a summary refetch.
        for (const proposal of this.pendingProposals) {
            const module = moduleForActionType(proposal.actionType);
            if (module === 'other') {
                continue;
            }
            const prior = counts.get(module) ?? { pending: 0, decided: 0 };
            counts.set(module, { pending: prior.pending + 1, decided: prior.decided });
        }
        // Only modules with items to review render a card — decided-only modules
        // have nothing to act on, and their history lives in Activity.
        return Array.from(counts.entries())
            .filter(([, c]) => c.pending > 0)
            .map(([module, c]) => {
                const meta = GABLE_MODULE_CARDS[module as Exclude<GableActivityFilter, 'all'>];

                return {
                    module: module as GableActivityFilter,
                    label: MODULE_LABELS[module as Exclude<GableActivityFilter, 'all'>],
                    description: `${c.pending} item${c.pending === 1 ? '' : 's'} to review`,
                    tagline: meta.tagline,
                    icon: meta.icon,
                    tone: meta.tone,
                };
            });
    }

    // Hero status strip — real counts (mock copy retired 2026-07-11).

    get heroStripPending(): number {
        return this.pendingProposals.length;
    }

    private get _totalDecided(): number {
        return this.activitySummary.reduce((sum, item) => sum + item.decided, 0);
    }

    /**
     * Show the "Needs your attention" strip ONLY when something is actually
     * pending. A zero-pending org has nothing to attend to — never render a
     * "View all 0 items" CTA just because items were decided in the past
     * (that history lives in Activity). Per Catherine 2026-07-15.
     */
    get showHeroStrip(): boolean {
        return this.heroStripPending > 0;
    }

    get heroStripTitle(): string {
        if (this.heroStripPending > 0) {
            return `${this.heroStripPending} item${this.heroStripPending === 1 ? '' : 's'} need your review`;
        }
        return `You're all caught up`;
    }

    get heroStripDesc(): string {
        if (this.heroStripPending > 0) {
            return 'Everything that needs you is in the review queue.';
        }
        return `${this._totalDecided} action${this._totalDecided === 1 ? '' : 's'} decided so far — the full history is in Activity.`;
    }

    /**
     * The hero strip is the single "needs you" owner (contract §5): when
     * anything is pending its CTA sends the user straight to the review queue.
     */
    get heroStripCta(): string {
        return this.heroStripPending > 0
            ? `View all ${this.heroStripPending} in the review queue`
            : 'View activity';
    }

    /** Fetch the per-module counts behind the hero quick links (best-effort). */
    private loadActivitySummary(): void {
        // Gable isn't enabled for this org (e.g. after switching to an org
        // without the flag) — skip the fetch so it doesn't 403 (SD-7937). Also
        // skip for anyone who can't see the drawer at all (SD-8321): the summary
        // is pure waste for them.
        //
        // Gated on `canUseGable`, NOT `isSuperUser` (SD-9015): this endpoint is
        // /proposals/activity-summary on GableController, which the beta allowlist
        // now reaches — so a super-only check here would blank the hero strip for
        // exactly the testers we invited. Contrast the feedback panel's
        // `isSuperUser` binding, which stays super-only because Teach/Flag write
        // GLOBAL lessons.
        // PMC scope bypasses the JWT-org flag — core checks the PMC's flags (SD-9074 wave 2).
        if ((!this._drawer.flagEnabled && !this._scope.isPmcScoped) || !this._drawer.canUseGable) {
            return;
        }
        this._aiPoc.getActivitySummary()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    this.activitySummary = res.items ?? [];
                    this._cdr.markForCheck();
                },
                error: () => undefined,
            });
    }

    trackByModuleLink(_: number, link: { module: string }): string {
        return link.module;
    }

    trackBySuggestionCard(index: number, card: GableSuggestionCardPayload): string {
        return `${index}:${card.title}`;
    }

    /** Suggestion-card item button → follow-up chat prompt asking the box to
     *  propose the action (which comes back as a confirmable preview card). */
    onSuggestionActionPrompt(prompt: string): void {
        if (this.isSending) {
            return;
        }
        this._drawer.sendMessage(prompt);
    }

    /** Time-of-day greeting for the empty-state hero. */
    get heroGreeting(): string {
        // Org timezone, not browser — matches the app-wide timezone rule.
        const tz = LegFiJwtService.getTimezone() || moment.tz.guess();
        const hour = moment.tz(tz).hour();
        const part = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
        return `Good ${part}, ${this.firstName}`;
    }

    // -------------------------------------------------------------------------
    // Chat history (SD-6742)
    // -------------------------------------------------------------------------

    /**
     * Header title for the chat view: the loaded conversation's title when one
     * is set, "New AI chat" on an empty fresh chat, otherwise "Gable AI".
     */
    get chatHeaderTitle(): string {
        // Expanded dialog: the title names the active VIEW (Figma) — the rail
        // owns navigation, so "Activity" reads as the pane heading there.
        if (this.isExpanded && this.activeTab === 'activity') {
            return 'Activity';
        }
        if (this.activeTitle && this.activeTitle.trim() !== '') {
            return this.activeTitle.trim();
        }
        return this.isEmptyState ? 'Chat with Gable AI' : 'Gable AI';
    }

    /** Id of the conversation currently loaded — drives the sidebar highlight. */
    get activeConversationId(): number | null {
        return this._drawer.conversationId;
    }

    /** Drawer-mode history menu shows only the most-recent N chats (kept basic). */
    get recentMenuConversations(): GableConversationSummary[] {
        return this.activeConversations.slice(0, GableDrawerHostComponent.DRAWER_MENU_RECENT_LIMIT);
    }

    /** Display label for one history row — title, else first-user-message hint. */
    conversationLabel(c: GableConversationSummary): string {
        const title = (c.title ?? '').trim();
        return title !== '' ? title : 'New chat';
    }

    /** Tenant label for a chat from another scope; empty when it belongs to the current one. */
    conversationScopeLabel(c: GableConversationSummary): string {
        const scope = c.scope;
        if (!scope?.id) {
            return '';
        }
        const here = this._scope.isPmcScoped && !this.drillDownOrg
            ? scope.kind === 'pmc' && scope.id === this._scope.pmcId
            : scope.kind === 'org' && scope.id === (this.drillDownOrg?.id ?? LegFiJwtService.read()?.orgId);
        if (here) {
            return '';
        }
        const name = (scope.name ?? '').trim();
        return scope.kind === 'pmc' ? `${name || 'Portfolio'} · portfolio` : name;
    }

    /** True when a search term is active — drives the empty-state copy branch. */
    get isSearchingHistory(): boolean {
        return this.historySearch.trim() !== '';
    }

    /** Sidebar chat-history time buckets — last 24h. */
    get recentConversations(): GableConversationSummary[] {
        return this.activeConversations.filter((c) => this._historyBucket(c) === 'recent');
    }

    /** Sidebar chat-history time buckets — 24-48h ago. */
    get yesterdayConversations(): GableConversationSummary[] {
        return this.activeConversations.filter((c) => this._historyBucket(c) === 'yesterday');
    }

    /** Sidebar chat-history time buckets — everything older than 48h. */
    get olderConversations(): GableConversationSummary[] {
        return this.activeConversations.filter((c) => this._historyBucket(c) === 'older');
    }

    private _historyBucket(c: GableConversationSummary): 'recent' | 'yesterday' | 'older' {
        const hoursAgo = moment().diff(moment(c.lastMessageAt), 'hours', true);
        if (hoursAgo < 24) { return 'recent'; }
        if (hoursAgo < 48) { return 'yesterday'; }
        return 'older';
    }

    /** Pull fresh history when the drawer-mode title menu opens. */
    onHistoryMenuOpened(): void {
        this._drawer.refreshConversations();
    }

    /**
     * Sidebar search input handler. Server-side now (SD-6742): the term is
     * pushed into the service's debounced (~300ms) switchMap pipeline, which
     * resets to page 1 and matches title OR message content. No client-side
     * filtering.
     */
    onHistorySearchChange(term: string): void {
        this.historySearch = term;
        this._drawer.searchConversations(term);
        this._cdr.markForCheck();
    }

    /**
     * Infinite-scroll for the recent-chats rail. When the list scrolls within
     * a threshold of the bottom AND the server says more pages exist, fetch the
     * next page (appended in the service). The service guards against
     * concurrent / no-more fetches, so spamming this is safe.
     */
    onSidebarListScroll(event: Event): void {
        if (!this.hasMoreConversations || this.conversationsLoading) {
            return;
        }
        const el = event.target as HTMLElement | null;
        if (!el) {
            return;
        }
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distanceFromBottom <= GableDrawerHostComponent.INFINITE_SCROLL_THRESHOLD_PX) {
            this._drawer.loadMoreConversations();
        }
    }

    onSelectConversation(c: GableConversationSummary): void {
        this.activeTab = 'chat';
        this._drawer.loadConversation(c.id, c.scope);
    }

    toggleShowArchived(): void {
        this.showArchived = !this.showArchived;
        // Lazy-load the archived list only when the section is expanded — it is NOT
        // fetched on drawer open, so a plain open stays a single GET /conversations.
        if (this.showArchived) {
            this._drawer.loadArchived();
        }
        this._cdr.markForCheck();
    }

    /** Load the archived list when the drawer-mode archived menu opens (lazy). */
    onArchivedMenuOpened(): void {
        this._drawer.loadArchived();
    }

    /**
     * Rename happens INLINE in the expanded sidebar (no modal — the expanded
     * drawer is itself a dialog, so a modal-on-modal reads badly). Clicking
     * Rename swaps the row's title for an input; commit on Enter/blur, cancel
     * on Escape. (Drawer mode has no rename affordance at all.)
     */
    onRenameConversation(c: GableConversationSummary): void {
        this.editingConversationId = c.id;
        this.editingTitle = this.conversationLabel(c);
        this._cdr.markForCheck();
        // Defer focus until the input has rendered in place of the title.
        setTimeout(() => {
            const input = document.querySelector<HTMLInputElement>('.gable-sidebar-rename-input');
            input?.focus();
            input?.select();
        });
    }

    /** Commit an inline rename. Guarded so a post-Escape blur is a no-op. */
    commitInlineRename(c: GableConversationSummary): void {
        if (this.editingConversationId !== c.id) {
            return;
        }
        const newTitle = this.editingTitle.trim();
        const current = this.conversationLabel(c);
        this.editingConversationId = null;
        this._cdr.markForCheck();

        if (newTitle === '' || newTitle === current) {
            return; // nothing to do
        }
        this._drawer
            .renameConversation(c.id, newTitle)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => this._growler.success('Chat renamed', 'The chat name was updated.'),
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    cancelInlineRename(): void {
        this.editingConversationId = null;
        this._cdr.markForCheck();
    }

    /**
     * Archive with no confirmation — just do it and toast it. We do NOT
     * auto-open the Archived section or highlight the row: that read as a false
     * "selected" state. The user opens Archived themselves to find it.
     */
    onArchiveConversation(c: GableConversationSummary): void {
        this._drawer
            .archiveConversation(c.id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => this._growler.success('Chat archived', 'Moved to archived chats.'),
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    /**
     * Restore = the user is deliberately bringing this chat back, so select it:
     * load it into the chat view (which makes it the active, bolded row in the
     * recent list) on top of moving it out of Archived.
     */
    onUnarchiveConversation(c: GableConversationSummary): void {
        this._drawer
            .unarchiveConversation(c.id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this._growler.success('Chat restored', 'Moved back to recent chats.');
                    this._drawer.loadConversation(c.id, c.scope);
                },
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    trackByConversationId(_: number, c: GableConversationSummary): number {
        return c.id;
    }

    /**
     * Toggle between drawer (right-edge slide) and expanded (full-width
     * centered dialog) modes. Persisted via UserPreferencesService so the
     * choice sticks across sessions.
     */
    onToggleDock(): void {
        this._drawer.toggleMode();
    }

    get isExpanded(): boolean {
        return this.mode === 'expanded';
    }

    // -------------------------------------------------------------------------
    // Composer
    // -------------------------------------------------------------------------

    onDraftChange(text: string): void {
        this._drawer.setDraft(text);
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.isComposing) {
            return;
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.onSend();
        }
    }

    onStopStream(): void {
        this._drawer.cancelStream();
    }

    // -------------------------------------------------------------------------
    // PDF attachment (SD-9309)
    // -------------------------------------------------------------------------

    onAttachClick(): void {
        if (this.attachmentUploading || this.pendingAttachment) {
            return;
        }
        this.attachmentFileInputRef?.nativeElement.click();
    }

    onAttachmentFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] ?? null;
        input.value = '';
        if (!file) {
            return;
        }
        this.attachmentUploading = true;
        this._cdr.markForCheck();
        this._attachmentUploader.uploadFile(file).pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.attachmentUploading = false;
                this._drawer.setPendingAttachment({ uploadedFileId: res.uploadedFileId, fileName: res.fileName });
                this._cdr.markForCheck();
            },
            error: (err) => {
                this.attachmentUploading = false;
                this._growler.oops(err?.message ?? 'Failed to upload file. Please try again.');
                this._cdr.markForCheck();
            },
        });
    }

    onRemovePendingAttachment(): void {
        this._drawer.clearPendingAttachment();
    }

    onSend(): void {
        const text = (this.draftMessage ?? '').trim();
        if (!text || (this.isSending && !this.backgroundMode)) {
            return;
        }
        this._drawer.sendMessage(text);
    }

    /** The running turn keeps working in its conversation; this question gets a fresh one. */
    askInNewChat(): void {
        const text = (this.draftMessage ?? '').trim();
        if (!text) {
            return;
        }
        this._drawer.startNewChatWithMessage(text);
    }

    onSuggestionClick(suggestion: GableSuggestion): void {
        if (this.isSending) {
            return;
        }
        this._drawer.sendMessage(suggestion.prompt);
    }

    /**
     * Click on an AI-supplied follow-up suggestion — sends the suggestion
     * text as the next user message. Mirrors the empty-state suggestion
     * click but takes a plain string (`apiSuggestions` is `string[]`).
     */
    onApiSuggestionClick(text: string): void {
        if (this.isSending) {
            return;
        }
        const trimmed = (text ?? '').trim();
        if (trimmed === '') {
            return;
        }
        this._drawer.sendMessage(trimmed);
    }

    trackByApiSuggestion(_: number, text: string): string {
        return text;
    }

    /**
     * Render the follow-up suggestion strip only under the most recent
     * assistant bubble (the Figma's "↳ Schedule for tomorrow / ↳ Add SMS
     * / ↳ Escalate to First Warning" rows). Returns true only when the
     * given message index is the last assistant message AND:
     *   - its stream is complete (don't show suggestions while typing)
     *   - it didn't emit an action-preview card. Suggestions on top of
     *     a still-pending action create the "I already approved" confusion
     *     (a suggestion that says "Switch to Final Notice" gets fired as
     *     a fresh user turn, which the model interprets as a new request
     *     instead of editing the current proposal). Action-preview cards
     *     own their own affordances — Edit, status dropdown, Add unit —
     *     so the follow-up strip is dead weight there.
     */
    isLastAssistantMessage(index: number): boolean {
        const msg = this.messages[index];
        if (!msg || msg.role !== 'assistant') {
            return false;
        }
        if (msg.streamState && msg.streamState !== 'complete') {
            return false;
        }
        if (this._hasActionPreviewCard(msg)) {
            return false;
        }
        for (let i = this.messages.length - 1; i > index; i -= 1) {
            if (this.messages[i].role === 'assistant') {
                return false;
            }
        }
        return true;
    }

    private _hasActionPreviewCard(msg: GableChatMessage): boolean {
        if (!msg.toolCards || msg.toolCards.length === 0) {
            return false;
        }
        // Suppress chips only while a proposal is PENDING (don't distract
        // from the confirm). Once committed/cancelled, the post-commit
        // "what next" chips should show under this message.
        return msg.toolCards.some((c) => {
            if (c.tool !== 'propose_action') {
                return false;
            }
            const state = (c.payload as { state?: string } | undefined)?.state;
            return state === undefined || state === 'pending';
        });
    }

    // -------------------------------------------------------------------------
    // Streaming bubble helpers (mirrors the Gable page's template hooks)
    // -------------------------------------------------------------------------

    trackByIndex(index: number): number {
        return index;
    }

    trackByProposalId(_: number, p: GablePendingProposal): number {
        return p.id;
    }

    trackBySuggestion(_: number, s: GableSuggestion): string {
        return s.label;
    }

    trackByCitation(_: number, c: GableCitation): string {
        return `${c.table}:${c.id}`;
    }

    trackByToolCard(index: number, _card: GableToolCard): number {
        return index;
    }

    asReportCardPayload(card: GableToolCard): GenerateReportCardPayload | null {
        if (card.tool !== 'generate_report') {
            return null;
        }
        if (!isGenerateReportCardPayload(card.payload)) {
            console.warn(
                `[gable-drawer-host] Skipping tool card with tool="${card.tool}": payload shape mismatch.`,
            );
            return null;
        }
        return card.payload;
    }

    asBudgetRedirectPayload(card: GableToolCard): BudgetRedirectCardPayload | null {
        if (card.tool !== 'redirect_to_budget') {
            return null;
        }
        if (!isBudgetRedirectCardPayload(card.payload)) {
            console.warn(
                `[gable-drawer-host] Skipping tool card with tool="${card.tool}": payload shape mismatch.`,
            );
            return null;
        }
        return card.payload;
    }

    asActionPreviewPayload(card: GableToolCard): ActionPreviewCardPayload | null {
        if (card.tool !== 'propose_action') {
            return null;
        }
        if (!isActionPreviewCardPayload(card.payload)) {
            console.warn(
                `[gable-drawer-host] Skipping tool card with tool="${card.tool}": payload shape mismatch.`,
            );
            return null;
        }
        return card.payload;
    }

    asDeliverableCardPayload(card: GableToolCard): DeliverableCardPayload | null {
        if (card.tool !== 'build_document') {
            return null;
        }
        if (!isDeliverableCardPayload(card.payload)) {
            console.warn(
                `[gable-drawer-host] Skipping tool card with tool="${card.tool}": payload shape mismatch.`,
            );
            return null;
        }
        return card.payload;
    }

    asLedgerIntelligencePayload(card: GableToolCard): LedgerIntelligenceResult | null {
        if (card.tool !== 'analyze_ledger') {
            return null;
        }
        if (!isLedgerIntelligenceCardPayload(card.payload)) {
            console.warn(
                `[gable-drawer-host] Skipping tool card with tool="${card.tool}": payload shape mismatch.`,
            );
            return null;
        }
        return card.payload.result;
    }

    onActionPreviewConfirm(event: { proposalId: number; edits: ActionPreviewEdits }): void {
        const idempotencyKey = this._randomUuid();
        this._drawer
            .commitActionProposal(event.proposalId, idempotencyKey, event.edits)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    // Patch the toolCard payload so the card re-renders
                    // into its "committed" final state via ngOnChanges,
                    // independent of any ViewChildren lookup timing. Carry
                    // the execute() result through so the collapsed view
                    // can show counts / deeplink to the destination.
                    this._drawer.patchActionPreviewState(
                        event.proposalId,
                        'committed',
                        res.result ?? null,
                    );
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    console.error('[gable-drawer-host] commit action failed', err);
                    const card = this._findActionPreviewCard(event.proposalId);
                    if (card) {
                        card.submitting = false;
                    }
                    if (isGableTerminalRefusal(err)) {
                        this._drawer.patchActionPreviewState(event.proposalId, 'cancelled');
                    }
                    // The chat-origin card had the same silent failure as the
                    // review queue — console only, nothing for the user, so a refused
                    // commit looked like a dead button here too.
                    this._growler.oops(gableCommitFailureMessage(err));
                    this._cdr.markForCheck();
                },
            });
    }

    onActionPreviewCancel(event: { proposalId: number }): void {
        this._drawer
            .rejectProposal(event.proposalId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this._drawer.patchActionPreviewState(event.proposalId, 'cancelled');
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    console.error('[gable-drawer-host] reject failed', err);
                    // Even on a server error, flip the card visually so
                    // the user gets feedback. Backend may have already
                    // recorded the proposal as rejected; worst case it's
                    // a stale "pending" row but the user moved on.
                    this._drawer.patchActionPreviewState(event.proposalId, 'cancelled');
                    this._cdr.markForCheck();
                },
            });
    }

    onActionPreviewUtility(event: {
        proposalId: number;
        utilityActionId: string;
        edits: ActionPreviewEdits;
    }): void {
        // `add_unit` is handled inline in the recipients section now —
        // the picker expands within the card and emits an `(edit)` patch
        // with `recipients.added_ids` directly. Nothing reaches this
        // handler with that id anymore. Other utility ids
        // (restore_skipped, future utility buttons) flow through.
        this._postRefine(event.proposalId, event.utilityActionId, event.edits);
    }

    private _postRefine(proposalId: number, utilityActionId: string, edits: ActionPreviewEdits): void {
        this._drawer
            .refineActionProposal(proposalId, utilityActionId, edits)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    // Patch the card payload in place inside the assistant
                    // message's toolCards. Backend has rewritten preview +
                    // edits — re-renders flow through the OnPush card.
                    this._drawer.patchActionPreview(res.proposalId, {
                        preview: res.preview,
                        edits: res.edits,
                    });
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    console.error('[gable-drawer-host] refine failed', err);
                },
            });
    }

    private _findActionPreviewCard(proposalId: number): GableActionPreviewCardComponent | undefined {
        return this.actionPreviewCardsRef?.find((c) => {
            const p = c.payload as unknown as Record<string, unknown> | null;
            return (p?.['proposal_id'] ?? p?.['proposalId']) === proposalId;
        });
    }

    private _randomUuid(): string {
        // crypto.randomUUID is widely supported in evergreen browsers.
        // Fallback to a timestamp+random string in the rare unsupported case.
        const c = window.crypto;
        if (c && typeof c.randomUUID === 'function') {
            return c.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    elapsedSeconds(msg: GableChatMessage): number | null {
        if (!msg.streamStartedAt || !msg.streamEndedAt) {
            return null;
        }
        return Math.max(1, Math.round((msg.streamEndedAt - msg.streamStartedAt) / 1000));
    }

    /**
     * True once the streaming answer has begun rendering a real, block-level
     * result (a markdown table or a list) — at which point the bottom-of-turn
     * "working" dots are retired, because the visible result is now the live
     * element. Plain prose alone does NOT count: preamble sentences and
     * quiet gaps between tool calls should keep the heartbeat alive.
     */
    isRenderingResults(content: string | null | undefined): boolean {
        if (!content) {
            return false;
        }
        // Markdown table row (`| … |`), a bullet/numbered list item at line
        // start, or a completed gable-suggestions card block (the card pops
        // in whole once its closing fence arrives).
        return /(^|\n)\s*\|.*\|/.test(content)
            || /(^|\n)\s*(?:[-*]|\d+\.)\s+\S/.test(content)
            || /```gable-suggestions[\s\S]*?```/.test(content);
    }

    onThoughtsCollapsedChange(messageId: number | undefined, collapsed: boolean): void {
        if (messageId === undefined) {
            return;
        }
        this._drawer.updateThoughtsCollapsed(messageId, collapsed);
    }

    // -------------------------------------------------------------------------
    // Question card
    // -------------------------------------------------------------------------

    onAnswers(answers: GableQuestionAnswer[]): void {
        const batch = this.pendingQuestions;
        if (!batch) {
            return;
        }
        const summary = this._summarizeAnswers(batch, answers);
        this._drawer.answerQuestions(answers, summary);
    }

    onCloseQuestionCard(): void {
        this._drawer.closeQuestionCard();
    }

    get submittingAnswers(): boolean {
        return this._drawer.submittingAnswers;
    }

    // -------------------------------------------------------------------------
    // Feedback
    // -------------------------------------------------------------------------

    getFeedback(messageId: number | undefined): GableFeedback | null {
        return this._drawer.getFeedback(messageId);
    }

    onFeedback(msg: GableChatMessage, feedback: 'good' | 'bad'): void {
        const messageId = msg.messageId;
        const interactionId = msg.interactionId;
        if (messageId === undefined || interactionId === undefined) {
            return;
        }
        const previous = this._drawer.getFeedback(messageId) ?? 'none';
        const next: GableFeedback = previous === feedback ? 'none' : feedback;
        this._drawer.setFeedback(messageId, next);
        this._drawer.recordFeedback(interactionId, next)
            .pipe(untilDestroyed(this))
            .subscribe({
                error: () => this._drawer.setFeedback(messageId, previous),
            });

        // Thumbs-down expands the inline feedback panel (reason chips +
        // Teach/Send paths). Thumbs-up or unsetting collapses it.
        if (feedback === 'bad' && next === 'bad') {
            this._feedbackPanelOpenFor = messageId;
        } else {
            this._feedbackPanelOpenFor = null;
        }
        this._cdr.markForCheck();
    }

    /** Whether the inline feedback panel is open for a given message. */
    isFeedbackPanelOpenFor(messageId: number | undefined): boolean {
        return messageId !== undefined && this._feedbackPanelOpenFor === messageId;
    }

    /** Cancel-all-the-panels helper (Skip path). */
    closeFeedbackPanel(): void {
        this._feedbackPanelOpenFor = null;
        this._cdr.markForCheck();
    }

    // --- Inline feedback panel event handlers ---

    onChipFeedback(msg: GableChatMessage, event: { chip: GableFeedbackChip; detail?: string }): void {
        const interactionId = msg.interactionId;
        if (interactionId === undefined) { return; }
        this._aiPoc.recordChipFeedback(interactionId, event.chip, event.detail)
            .pipe(untilDestroyed(this))
            .subscribe();
        // Lock further feedback on this interaction and swap the thumbs
        // row out for the green "submitted" confirmation.
        this._drawer.markFeedbackSent(interactionId);
        this.closeFeedbackPanel();
        this._cdr.markForCheck();
    }

    onTeachRequestedFromChip(
        msg: GableChatMessage,
        event: { chip: GableFeedbackChip },
    ): void {
        // NOTE: do NOT markFeedbackSent here. We don't want the green
        // confirmation banner appearing while the user is still in the
        // middle of writing the lesson — only after they close the
        // Teach view (save or cancel).
        this._openTeachGableDrawer(msg, event.chip, false);
        this.closeFeedbackPanel();
    }

    /**
     * Flag for team to review — telemetry-only. Records the chip on the
     * interaction event and marks feedback as sent. No lesson row, no
     * drawer. The team can find these via `metadata.feedback_chip` with
     * thumbs=bad in their dashboard.
     */
    onFlagRequestedFromChip(
        msg: GableChatMessage,
        event: { chip: GableFeedbackChip },
    ): void {
        const interactionId = msg.interactionId;
        if (interactionId === undefined) { return; }
        this._aiPoc
            .recordChipFeedback(interactionId, event.chip, '[flagged for team review]')
            .pipe(untilDestroyed(this))
            .subscribe();
        this._drawer.markFeedbackSent(interactionId);
        this.closeFeedbackPanel();
        this._cdr.markForCheck();
    }

    /** Renders the green "feedback submitted" confirmation instead of thumbs. */
    wasFeedbackSent(msg: GableChatMessage): boolean {
        return this._drawer.wasFeedbackSent(msg.interactionId);
    }

    /**
     * "Teach Gable from this answer" — the in-chat capture point that
     * sits next to the 👍/👎 icons on every assistant turn. Different
     * semantics from the feedback-chip path:
     *
     *   - **Trigger**: the conversation's FIRST user message (the
     *     original question users will ask again), NOT the message
     *     immediately preceding `msg`. If turn 1 was "what are total
     *     late fees?" and turn 2 was the user pushing back, we still
     *     want future "what are total late fees?" askers to hit this
     *     lesson — so the trigger lives on turn 1's prompt.
     *   - **Guidance seed**: the FULL content of `msg` (the corrected
     *     answer). Author edits/trims before save.
     *   - **Source event**: `msg.interactionId` so the backend can
     *     stamp `ai_interaction_events.promoted_lesson_id` and the
     *     AI Activity drawer renders "Already promoted" if the admin
     *     finds this turn there later.
     */
    onTeachFromThisAnswer(msg: GableChatMessage): void {
        const firstUserMsg = this.messages.find((m) => m.role === 'user');
        const triggerContext = (firstUserMsg?.content ?? '').trim();
        const guidance = (msg.content ?? '').trim();
        if (triggerContext === '' || guidance === '') {
            // Nothing useful to seed with — bail quietly. (Should be rare:
            // user has to be on a complete assistant message AND there
            // has to be a user message earlier in the conversation.)
            return;
        }
        const reasoningTrail = (msg.thoughts ?? [])
            .filter((t) => t.kind === 'summary')
            .map((t) => t.label);
        const data: TeachGableDialogData = {
            userQuestion: triggerContext,
            assistantAnswer: guidance,
            reasoningTrail,
            prefillDraft: {
                title: this._makeDraftTitle(triggerContext),
                triggerContext,
                guidance,
            },
            ...(msg.interactionId !== undefined ? { fromInteractionEventId: msg.interactionId } : {}),
        };

        if (this._drawer.mode === 'drawer') {
            this._pendingTeachMsg = msg;
            this._drawer.openTeachInDrawer(data);
            this._cdr.markForCheck();
            return;
        }

        this._dialog
            .open<TeachGableDialogComponent, TeachGableDialogData, TeachGableDialogResult>(
                TeachGableDialogComponent,
                {
                    panelClass: ['gable-teach-drawer'],
                    width: MatDialogSizes.MD,
                    data,
                },
            )
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result?.lesson && msg.interactionId !== undefined) {
                    this._drawer.markTaught(msg.interactionId);
                }
            });
    }

    /**
     * Mirror of the backend `AiActivityController::makeDraftTitle` —
     * keeps the in-chat capture's title seed consistent with the
     * Activity-page promote flow. First ~60 chars of the prompt,
     * ending at the last word boundary.
     */
    private _makeDraftTitle(prompt: string): string {
        const trimmed = prompt.trim();
        if (trimmed.length <= 60) { return trimmed; }
        const slice = trimmed.slice(0, 60);
        const lastSpace = slice.lastIndexOf(' ');
        return (lastSpace > 30 ? slice.slice(0, lastSpace) : slice).trim();
    }

    /**
     * Open the Teach Gable drawer for one assistant message. Used by the
     * inline feedback panel (Teach / Flag CTAs); also still works as an
     * explicit "Teach about this" button if needed elsewhere.
     */
    private _openTeachGableDrawer(
        msg: GableChatMessage,
        triggeringChip?: GableFeedbackChip,
        _startInFlagMode: boolean = false,
    ): void {
        const userQuestion = this.findPrecedingUserMessage(msg);
        const reasoningTrail = (msg.thoughts ?? [])
            .filter((t) => t.kind === 'summary')
            .map((t) => t.label);
        const data: TeachGableDialogData = {
            userQuestion,
            assistantAnswer: msg.content ?? '',
            reasoningTrail,
            triggeringChip,
        };

        // In drawer mode swap the drawer's body to the Teach view (no
        // stacked overlays). In expanded / dialog mode open Teach as its
        // own MatDialog — the underlying page still shows so there's no
        // awkward drawer-on-drawer.
        if (this._drawer.mode === 'drawer') {
            this._pendingTeachMsg = msg;
            this._drawer.openTeachInDrawer(data);
            this._cdr.markForCheck();
            return;
        }

        this._dialog
            .open<TeachGableDialogComponent, TeachGableDialogData, TeachGableDialogResult>(
                TeachGableDialogComponent,
                {
                    panelClass: ['gable-teach-drawer'],
                    data,
                    autoFocus: false,
                    restoreFocus: false,
                    hasBackdrop: false,
                    disableClose: true,
                },
            )
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => this._handleTeachClosed(result ?? null, msg));
    }

    /**
     * Bound on the inline `<app-teach-gable-dialog>` instance — fires
     * whatever the embedded form emits (save / cancel / flag).
     */
    onTeachClosed(result: TeachGableDialogResult): void {
        const msg = this._pendingTeachMsg;
        this._drawer.closeTeachInDrawer();
        this._pendingTeachMsg = null;
        this._handleTeachClosed(result, msg);
    }

    private _handleTeachClosed(result: TeachGableDialogResult, msg: GableChatMessage | null): void {
        if (msg?.interactionId !== undefined) {
            if (result) {
                // Saved or flagged — mark taught (for the 🎓 badge) AND
                // mark feedback-sent (for the green confirmation alert).
                this._drawer.markTaught(msg.interactionId);
                this._drawer.markFeedbackSent(msg.interactionId);

                // The MariaDB row is canonical; if the pgvector embed
                // failed the lesson is "saved" but won't fire in chat
                // retrieval until manually reindexed. Surface that at
                // save-time instead of waiting for the admin to notice
                // via the management page "needs reindex" cue.
                if (result.lesson?.embedStatus === 'pending') {
                    this._growler.error(
                        'Gable lessons',
                        'Lesson saved, but the vector index could not be built. A manual reindex is needed before Gable will use it.',
                    );
                }
            }
            // Cancelled (result is null): do nothing — leave the user
            // free to re-thumbs-down and try again.
        }
        this._cdr.markForCheck();
    }

    wasTaught(msg: GableChatMessage): boolean {
        return this._drawer.wasTaught(msg.interactionId);
    }

    trackByAppliedLessonId(_idx: number, l: { id: number }): number {
        return l.id;
    }

    /**
     * Walk backwards from the assistant turn that got the thumbs-down to
     * find the last user message — that's the question Gable was answering.
     */
    private findPrecedingUserMessage(target: GableChatMessage): string {
        const all = this._drawer.messages;
        const idx = all.indexOf(target);
        if (idx < 0) { return ''; }
        for (let i = idx - 1; i >= 0; i--) {
            const m = all[i];
            if (m.role === 'user' && m.content.trim() !== '') {
                return m.content;
            }
        }
        return '';
    }

    get isSuperUser(): boolean {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        return !!jwt?.superUser;
    }

    // -------------------------------------------------------------------------
    // Proposals
    // -------------------------------------------------------------------------

    get proposalInFlight(): number | null {
        return this._drawer.proposalInFlight;
    }

    onReviewProposal(proposal: GablePendingProposal): void {
        if (this._drawer.proposalInFlight === proposal.id) {
            return;
        }
        const data: ApprovalDialogData = { proposal };
        this._dialog
            .open<ApprovalDialogComponent, ApprovalDialogData, ApprovalDialogResult>(
                ApprovalDialogComponent,
                {
                    // gable-v2-dialog, NOT v2-dialog: the drawer lives outside
                    // the v2-layout host, so the scoped .v2-dialog reset never
                    // reaches the CDK overlay (see styles/ui/app.scss).
                    panelClass: ['gable-v2-dialog'],
                    width: MatDialogSizes.SM,
                    data,
                    autoFocus: false,
                },
            )
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result === 'approve') {
                    this._commitProposal(proposal);
                } else if (result === 'reject') {
                    this._rejectProposal(proposal);
                }
            });
    }

    onCancelProposal(proposal: GablePendingProposal): void {
        if (this._drawer.proposalInFlight === proposal.id) {
            return;
        }
        this._rejectProposal(proposal);
    }

    private _commitProposal(proposal: GablePendingProposal): void {
        const idempotencyKey = crypto.randomUUID();
        this._drawer.proposalInFlight = proposal.id;
        this._cdr.markForCheck();
        this._drawer.approveProposal(proposal.id, idempotencyKey)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    this._drawer.proposalInFlight = null;
                    this._drawer.removeProposal(proposal.id);
                    const friendly = this._friendlyActionDescription(proposal);
                    const note = res.idempotent
                        ? `${friendly} — already done previously, no duplicate created.`
                        : `${friendly}.`;
                    this._drawer.appendMessage({
                        role: 'assistant',
                        content: note,
                        timestamp: new Date(),
                        alert: 'success',
                    });
                },
                error: () => {
                    this._drawer.proposalInFlight = null;
                    this._drawer.appendMessage({
                        role: 'assistant',
                        content: 'Sorry — committing that proposal failed. It may have expired or been rejected by another tab. Try again, or ask the assistant to repropose.',
                        timestamp: new Date(),
                    });
                },
            });
    }

    private _rejectProposal(proposal: GablePendingProposal): void {
        this._drawer.proposalInFlight = proposal.id;
        this._cdr.markForCheck();
        this._drawer.rejectProposal(proposal.id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this._drawer.proposalInFlight = null;
                    this._drawer.removeProposal(proposal.id);
                    this._drawer.appendMessage({
                        role: 'assistant',
                        content: `Cancelled the ${proposal.operation} on ${proposal.modelClass}. Nothing was changed.`,
                        timestamp: new Date(),
                        alert: 'info',
                    });
                },
                error: () => {
                    this._drawer.proposalInFlight = null;
                    this._cdr.markForCheck();
                },
            });
    }

    private _friendlyActionDescription(proposal: GablePendingProposal): string {
        const verb = {
            create: 'created',
            update: 'updated',
            delete: 'deleted',
        }[proposal.operation] ?? proposal.operation;
        const payload = proposal.payload ?? {};
        const primary = (payload['name'] ?? payload['title'] ?? null) as string | null;
        const friendlyModel = proposal.modelClass === 'ViolationStatement' ? 'Violation' : proposal.modelClass;
        if (primary) {
            return `${friendlyModel} "${primary}" ${verb}`;
        }
        if (proposal.entityId !== null) {
            return `${friendlyModel} #${proposal.entityId} ${verb}`;
        }
        return `${friendlyModel} ${verb}`;
    }

    private _summarizeAnswers(
        batch: GablePendingQuestions,
        answers: GableQuestionAnswer[],
    ): string {
        const byId = new Map(batch.questions.map((q) => [q.id, q]));
        let parts: string[] = [];
        for (const a of answers) {
            const q = byId.get(a.id);
            if (!q) {
                continue;
            }
            const label = q.prompt.replace(/[?:]+\s*$/, '');
            const optById = new Map((q.options ?? []).map((o) => [o.value, o.label]));
            let valueText: string;
            if (Array.isArray(a.value)) {
                valueText = a.value
                    .map((v) => optById.get(String(v)) ?? String(v))
                    .filter((v) => v !== '')
                    .join(', ');
            } else if (typeof a.value === 'number') {
                valueText = String(a.value);
            } else {
                valueText = optById.get(a.value) ?? a.value;
            }
            if (valueText !== '') {
                parts = [...parts, `${label}: ${valueText}`];
            }
        }
        return parts.join(' · ');
    }
}
